/**
 * In-app road routing for the rider app.
 * Prefer Mapbox (working), then ORS (go7 legacy), then straight line.
 */

import { ORS_API_KEY } from './maptilerConfig';
import type { LatLng } from './locationSmoothing';
import { distanceMeters } from './locationSmoothing';

export type RouteResult = {
  path: LatLng[];
  distanceMeters: number;
  durationMinutes: number;
  distanceLabel: string;
  etaLabel: string;
  source: 'mapbox' | 'ors' | 'straight';
};

const CACHE_KEY = 'go7-route-cache-v2';
const DAY_KEY = 'go7-route-day';
const COUNT_KEY = 'go7-route-count';
const DAILY_BUDGET = 150;
const CACHE_TTL_MS = 45 * 60 * 1000;

const MAPBOX_TOKEN =
  import.meta.env.VITE_MAPBOX_TOKEN ||
  'pk.eyJ1IjoiZ283LSIsImEiOiJjbWVvejIzbTkxNGhoMndzYzh4eG5vMXJrIn0.fxFizCUQNCane2JyUcnUNg';

function todayStamp(): string {
  return new Date().toISOString().slice(0, 10);
}

function readDayCount(): number {
  try {
    if (localStorage.getItem(DAY_KEY) !== todayStamp()) {
      localStorage.setItem(DAY_KEY, todayStamp());
      localStorage.setItem(COUNT_KEY, '0');
      return 0;
    }
    return Number(localStorage.getItem(COUNT_KEY) || '0') || 0;
  } catch {
    return 0;
  }
}

function bumpDayCount(): void {
  try {
    localStorage.setItem(DAY_KEY, todayStamp());
    localStorage.setItem(COUNT_KEY, String(readDayCount() + 1));
  } catch {
    /* ignore */
  }
}

function cacheKey(a: LatLng, b: LatLng): string {
  return `${a.lat.toFixed(3)},${a.lng.toFixed(3)}|${b.lat.toFixed(3)},${b.lng.toFixed(3)}`;
}

function readCache(): Record<string, RouteResult & { savedAt: number }> {
  try {
    return JSON.parse(sessionStorage.getItem(CACHE_KEY) || '{}');
  } catch {
    return {};
  }
}

function writeCache(cache: Record<string, RouteResult & { savedAt: number }>): void {
  try {
    const entries = Object.entries(cache);
    const trimmed =
      entries.length > 40
        ? Object.fromEntries(entries.sort((x, y) => y[1].savedAt - x[1].savedAt).slice(0, 40))
        : cache;
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(trimmed));
  } catch {
    /* ignore */
  }
}

function formatRoute(path: LatLng[], meters: number, minutes: number, source: RouteResult['source']): RouteResult {
  return {
    path,
    distanceMeters: meters,
    durationMinutes: minutes,
    distanceLabel: meters >= 1000 ? `${(meters / 1000).toFixed(1)} km` : `${Math.round(meters)} m`,
    etaLabel: `${Math.max(1, Math.round(minutes))} min`,
    source,
  };
}

function straightRoute(origin: LatLng, destination: LatLng): RouteResult {
  const meters = distanceMeters(origin, destination);
  const minutes = Math.max(1, meters / 250);
  return formatRoute([origin, destination], meters, minutes, 'straight');
}

async function fetchMapbox(origin: LatLng, destination: LatLng): Promise<RouteResult | null> {
  const url =
    `https://api.mapbox.com/directions/v5/mapbox/driving/` +
    `${origin.lng},${origin.lat};${destination.lng},${destination.lat}` +
    `?geometries=geojson&overview=full&access_token=${MAPBOX_TOKEN}`;

  const response = await fetch(url);
  if (!response.ok) throw new Error(`Mapbox ${response.status}`);
  const data = await response.json();
  const route = data?.routes?.[0];
  const coords: number[][] = route?.geometry?.coordinates || [];
  if (!coords.length) return null;

  const path: LatLng[] = coords.map(([lng, lat]) => ({ lat, lng }));
  const meters = Number(route.distance) || distanceMeters(origin, destination);
  const minutes = (Number(route.duration) || 0) / 60 || Math.max(1, meters / 250);
  return formatRoute(path, meters, minutes, 'mapbox');
}

async function fetchOrs(origin: LatLng, destination: LatLng): Promise<RouteResult | null> {
  const response = await fetch('https://api.openrouteservice.org/v2/directions/driving-car/geojson', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: ORS_API_KEY,
      Accept: 'application/json, application/geo+json',
    },
    body: JSON.stringify({
      coordinates: [
        [origin.lng, origin.lat],
        [destination.lng, destination.lat],
      ],
    }),
  });

  if (!response.ok) throw new Error(`ORS ${response.status}`);
  const data = await response.json();
  const feature = data?.features?.[0];
  const coords: number[][] = feature?.geometry?.coordinates || [];
  const summary = feature?.properties?.summary;
  if (!coords.length) return null;

  const path: LatLng[] = coords.map(([lng, lat]) => ({ lat, lng }));
  const meters = Number(summary?.distance) || distanceMeters(origin, destination);
  const minutes = (Number(summary?.duration) || 0) / 60 || Math.max(1, meters / 250);
  return formatRoute(path, meters, minutes, 'ors');
}

/** Gated road route for in-app navigation. */
export async function fetchInAppRoute(
  origin: LatLng,
  destination: LatLng,
  opts?: { force?: boolean }
): Promise<RouteResult> {
  const key = cacheKey(origin, destination);
  const cache = readCache();
  const hit = cache[key];
  if (hit && !opts?.force && Date.now() - hit.savedAt < CACHE_TTL_MS) {
    return hit;
  }

  if (readDayCount() >= DAILY_BUDGET) {
    return hit || straightRoute(origin, destination);
  }

  bumpDayCount();

  try {
    const route = await fetchMapbox(origin, destination);
    if (route) {
      cache[key] = { ...route, savedAt: Date.now() };
      writeCache(cache);
      return route;
    }
  } catch (err) {
    console.warn('[route] Mapbox failed, trying ORS:', err);
  }

  try {
    const route = await fetchOrs(origin, destination);
    if (route) {
      cache[key] = { ...route, savedAt: Date.now() };
      writeCache(cache);
      return route;
    }
  } catch (err) {
    console.warn('[route] ORS failed, using straight line:', err);
  }

  const fallback = straightRoute(origin, destination);
  cache[key] = { ...fallback, savedAt: Date.now() };
  writeCache(cache);
  return fallback;
}

export function shouldRefetchInAppRoute(opts: {
  lastOrigin: LatLng | null;
  nextOrigin: LatLng;
  lastFetchedAt: number;
  hasPath: boolean;
  destinationChanged: boolean;
}): boolean {
  if (opts.destinationChanged) return true;
  if (!opts.hasPath || !opts.lastOrigin) return true;
  if (distanceMeters(opts.lastOrigin, opts.nextOrigin) < 400) return false;
  if (Date.now() - opts.lastFetchedAt < 90_000) return false;
  return true;
}
